// import libraries
import React, { memo, useMemo, useState, useCallback } from "react";
import { Button } from "primereact/button";
import { GoogleMap } from "@react-google-maps/api";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { Tooltip } from "primereact/tooltip";
import { Toast } from "primereact/toast";
import { Divider } from "primereact/divider";

// import content
import CONTENT from "./utils/content";

// import components
import SignInButton from "./components/SignInButton";
import DemoSection from "./components/DemoSection";
import AccessError from "./components/AccessError";
import FeaturesSection from "./components/FeaturesSection";
import AboutSection from "./components/AboutSection";
import Footer from "./components/Footer";

// import hooks
import { withLandingLogic } from "./withLandingLogic";
import useWhitelistAccess from "../../../hooks/useWhitelistAccess";
import { hasUserLimitBeenReached } from "../../../utils/accessControl";

// import constants
import { FEATURE_LIST, ABOUT_CARDS, BRAND_NAME } from "./constant";

// import styling
import "./style.css";

// import utils
import { bionicText } from "../../../utils/bionicText";

// routing
import { useNavigate } from "react-router-dom";

const LandingPage = ({
  isMobile,
  isLoading,
  accessError,
  toast,
  demoChatUrl,
  handleGoogleSignIn,
  handleTryDemo,
  bionicEnabled,
  toggleBionic,
}) => {
  const navigate = useNavigate();
  const [userLimitReached, setUserLimitReached] = React.useState(false);
  const {
    requestAccess,
    isLoading: isRequestingAccess,
    error: requestError,
    storedEmail,
    clearStoredRequest,
  } = useWhitelistAccess();
  // Add state to control whether to show the form
  const [showEmailForm, setShowEmailForm] = useState(false);
  // Hero search input state
  const [heroSearch, setHeroSearch] = useState("");

  React.useEffect(() => {
    const checkUserLimit = async () => {
      try {
        const limitReached = await hasUserLimitBeenReached();
        setUserLimitReached(limitReached);
      } catch (error) {
        console.error("Error checking user limit:", error);
      }
    };

    checkUserLimit();
  }, []);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  const handleRequestAccess = useCallback(
    async (email) => {
      const trimmedEmail = email.trim();
      if (!trimmedEmail) {
        toast.current.show({
          severity: "error",
          summary: CONTENT.toast.error,
          detail: CONTENT.formMessages.requestError,
          life: 3000,
        });
        return;
      }

      if (!validateEmail(trimmedEmail)) {
        toast.current.show({
          severity: "error",
          summary: CONTENT.toast.error,
          detail: CONTENT.formMessages.invalidEmail,
          life: 3000,
        });
        return;
      }

      const success = await requestAccess(trimmedEmail);
      if (success) {
        toast.current.show({
          severity: "success",
          summary: CONTENT.toast.success,
          detail: CONTENT.formMessages.requestSuccess,
          life: 3000,
        });
        setShowEmailForm(false);
      }
    },
    [requestAccess]
  );

  // Memoize the main content to prevent unnecessary re-renders
  const heroSection = useMemo(
    () => (
      <div className="hero-section-container">
        <div className="hero-map-container">
          <GoogleMap
            mapContainerStyle={{
              width: "100%",
              height: "100%",
            }}
            zoom={12}
            center={{ lat: 14.5995, lng: 120.9842 }}
            options={{
              disableDefaultUI: true,
              zoomControl: false,
              mapTypeControl: false,
              scaleControl: false,
              streetViewControl: false,
              rotateControl: false,
              fullscreenControl: false,
              styles: [
                {
                  featureType: "poi",
                  elementType: "labels",
                  stylers: [{ visibility: "off" }],
                },
              ],
            }}
          />
        </div>
        <div className="hero-section">
          <h1>
            {bionicEnabled
              ? bionicText(CONTENT.hero.welcome)
              : CONTENT.hero.welcome}
          </h1>
          <p className="hero-subtitle">
            {bionicEnabled
              ? bionicText(CONTENT.hero.subtitle)
              : CONTENT.hero.subtitle}
          </p>

          <div className="hero-search-container">
            <i className="pi pi-search hero-search-icon" />
            <input
              type="text"
              className="hero-search-input"
              placeholder="Search for businesses..."
              value={heroSearch}
              onChange={(e) => setHeroSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const q = heroSearch.trim();
                  if (q.length > 0) {
                    navigate(`/map?business=${encodeURIComponent(q)}`);
                  }
                }
              }}
            />
          </div>

          <Divider />
          <div className="cta-container">
            {!userLimitReached ? (
              <>
                <SignInButton
                  isLoading={isLoading}
                  accessError={accessError}
                  handleGoogleSignIn={handleGoogleSignIn}
                  bionicEnabled={bionicEnabled}
                />
                {accessError && <AccessError error={accessError} />}
              </>
            ) : storedEmail && !showEmailForm ? (
              <div className="access-requested">
                <p>
                  {CONTENT.formMessages.requestedAccess.replace(
                    "{email}",
                    storedEmail.email
                  )}
                </p>
                <Button
                  label={CONTENT.formMessages.changeEmail}
                  className="p-button-text"
                  onClick={() => {
                    setShowEmailForm(true);
                    const emailInput = document.querySelector(
                      ".access-email-input"
                    );
                    if (emailInput) {
                      emailInput.value = "";
                    }
                  }}
                />
              </div>
            ) : (
              <div className="user-limit-reached">
                <p>{CONTENT.formMessages.userLimit}</p>
                <p>{CONTENT.formMessages.emailRequest}</p>
                <div className="access-request-form">
                  <InputText
                    type="email"
                    placeholder={CONTENT.formMessages.emailPlaceholder}
                    className="access-email-input"
                    onKeyPress={(e) => {
                      if (e.key === "Enter" && e.target.value) {
                        handleRequestAccess(e.target.value.trim());
                      }
                    }}
                  />
                  <Button
                    label={CONTENT.formMessages.requestAccess}
                    className="p-button-primary"
                    onClick={() => {
                      const emailInput = document.querySelector(
                        ".access-email-input"
                      );
                      if (emailInput && emailInput.value) {
                        handleRequestAccess(emailInput.value.trim());
                      }
                    }}
                    loading={isRequestingAccess}
                    disabled={isRequestingAccess}
                  />
                  {requestError && (
                    <div className="request-error">{requestError}</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    ),
    [
      bionicEnabled,
      isLoading,
      accessError,
      userLimitReached,
      storedEmail,
      showEmailForm,
      isRequestingAccess,
      requestError,
      handleGoogleSignIn,
      handleRequestAccess,
      heroSearch,
      navigate,
    ]
  );

  const content = useMemo(
    () => (
      <div className="landing-content">
        <FeaturesSection
          bionicEnabled={bionicEnabled}
          FEATURE_LIST={FEATURE_LIST}
        />

        <AboutSection
          bionicEnabled={bionicEnabled}
          ABOUT_CARDS={ABOUT_CARDS}
          BRAND_NAME={BRAND_NAME}
        />
      </div>
    ),
    [
      isMobile,
      isLoading,
      accessError,
      demoChatUrl,
      handleGoogleSignIn,
      handleTryDemo,
      bionicEnabled,
      userLimitReached,
      storedEmail,
      showEmailForm,
      isRequestingAccess,
      requestError,
      handleRequestAccess,
    ]
  );

  return (
    <div className="landing-page">
      <div
        className="bionic-switch-floating"
        style={{
          backgroundColor: "var(--surface-0)",
          borderRadius: "50px",
          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
          position: "fixed",
          top: "1rem",
          right: "1rem",
          zIndex: 1000,
          padding: "0.75rem 1.25rem",
          display: "flex",
          alignItems: "center",
          gap: "0.75rem",
          minWidth: 180,
        }}
        data-pr-tooltip={CONTENT.bionic.label}
        data-pr-position="top"
        id="bionic-tooltip-switch"
      >
        <span style={{ fontWeight: 500 }}>
          {bionicEnabled
            ? bionicText(CONTENT.bionic.label)
            : CONTENT.bionic.label}
        </span>
        <InputSwitch
          checked={bionicEnabled}
          onChange={(e) => toggleBionic(e.value)}
        />
      </div>
      <Tooltip
        target="#bionic-tooltip-switch"
        content={() =>
          bionicEnabled ? (
            <span>{bionicText(CONTENT.bionic.tooltip)}</span>
          ) : (
            CONTENT.bionic.tooltip
          )
        }
      />
      <Toast ref={toast} />
      {heroSection}
      {content}
      <Footer
        bionicEnabled={bionicEnabled}
        copyright={CONTENT.footer.copyright.replace(
          "{year}",
          new Date().getFullYear()
        )}
      />
    </div>
  );
};

export default withLandingLogic(memo(LandingPage));
